import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { fireDB } from '../../../Backend/Firebase/FirebaseConfigData';

interface Subtopic {
  id: string;
  title: string;
  pdfUrl?: string;
  driveLink?: string;
  blogLink?: string;
  dataType?: string;
  metadata?: string;
  author?: string;
  createdAt?: unknown;
}

interface EntryDetailProps {
  entryId: string;
  onBack: () => void;
}

const EntryDetailPage: React.FC<EntryDetailProps> = ({ entryId, onBack }) => {
  const [entryData, setEntryData] = useState<any>(null);
  const [subtopics, setSubtopics] = useState<Subtopic[]>([]);

  useEffect(() => {
    const fetchEntryAndSubs = async () => {
      try {
        const docRef = doc(fireDB, 'libraryEntries', entryId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setEntryData(docSnap.data());
        }

        const subCol = collection(fireDB, 'libraryEntries', entryId, 'subtopics');
        const q = query(subCol);
        const snapshot = await getDocs(q);
        const subs: Subtopic[] = [];
        snapshot.forEach((d) => {
          subs.push({ id: d.id, ...(d.data() as Omit<Subtopic, 'id'>) });
        });
        setSubtopics(subs);
      } catch (err) {
        console.error('Error fetching entry or subtopics:', err);
      }
    };

    fetchEntryAndSubs();
  }, [entryId]);

  const formatDate = (ts: any) => {
    if (ts && ts.toDate) return ts.toDate().toLocaleString();
    return 'Not found';
  };

  if (!entryData) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="mb-4 text-blue-600">← Back</button>
        <p>Loading entry...</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <button onClick={onBack} className="mb-4 text-blue-600 hover:underline">← Back</button>

      {/* Main Entry Card */}
      <div className="bg-white p-6 rounded shadow mb-8">
        <h2 className="text-2xl font-semibold mb-2">{entryData.title}</h2>
        <p>Type: {entryData.dataType || 'Not found'}</p>
        <p>Author: {entryData.author || 'Not found'}</p>
        <p>Date: {formatDate(entryData.createdAt)}</p>
        <p>Metadata: {entryData.metadata || 'None'}</p>

        <div className="mt-4 space-y-2">
          {entryData.blogLink && (
            <div>
              📝 Blog:{" "}
              <a
                href={entryData.blogLink}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                Open Blog
              </a>
            </div>
          )}
          {entryData.driveLink && (
            <div>
              🔗 Drive:{" "}
              <a
                href={entryData.driveLink}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                Open Drive Link
              </a>
            </div>
          )}
          {entryData.pdfUrl && (
            <div>
              📄 PDF:{" "}
              <a
                href={entryData.pdfUrl}
                target="_blank"
                download
                rel="noreferrer"
                className="text-green-600 underline"
              >
                Download PDF
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Subtopics */}
      <div className="bg-white p-6 rounded shadow">
        <h3 className="text-xl font-semibold mb-4">📑 Subtopics</h3>
        {subtopics.length === 0 ? (
          <p>No subtopics available.</p>
        ) : (
          <table className="w-full table-auto border">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="p-2 border">Title</th>
                <th className="p-2 border">Type</th>
                <th className="p-2 border">PDF</th>
                <th className="p-2 border">Drive</th>
                <th className="p-2 border">Blog</th>
              </tr>
            </thead>
            <tbody>
              {subtopics.map((sub) => (
                <tr key={sub.id} className="border-t">
                  <td className="p-2 border">{sub.title}</td>
                  <td className="p-2 border">{sub.dataType || '—'}</td>
                  <td className="p-2 border">
                    {sub.pdfUrl ? (
                      <a
                        href={sub.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        download
                        className="text-green-600 underline"
                      >
                        Download PDF
                      </a>
                    ) : '—'}
                  </td>
                  <td className="p-2 border">
                    {sub.driveLink ? (
                      <a
                        href={sub.driveLink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline"
                      >
                        Drive
                      </a>
                    ) : '—'}
                  </td>
                  <td className="p-2 border">
                    {sub.blogLink ? (
                      <a
                        href={sub.blogLink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline"
                      >
                        Blog
                      </a>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EntryDetailPage;
